import React, { createContext, useContext, useState, useEffect } from 'react';
import posthog from 'posthog-js';

const API = process.env.REACT_APP_API_URL || 'https://uscope-v2.vercel.app';
const API_KEY = process.env.REACT_APP_API_KEY || '';

// PostHog: initialise once. persistence:'localStorage' = cross-session analytics
// without cookies (cookieless tracking, still ePrivacy-compatible in most jurisdictions).
if (process.env.REACT_APP_POSTHOG_KEY) {
  posthog.init(process.env.REACT_APP_POSTHOG_KEY, {
    api_host: 'https://app.posthog.com',
    persistence: 'localStorage',
    autocapture: true,
    capture_pageview: true,
  });
}

function getOrCreateSessionId() {
  let id = localStorage.getItem('uscope_session_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('uscope_session_id', id);
  }
  return id;
}

const UserContext = createContext(null);

export function UserProvider({ children }) {
  const [sessionId] = useState(getOrCreateSessionId);
  const [profile, setProfile] = useState(() => {
    // Prefer localStorage (persists across browser closes) over sessionStorage
    try {
      const lsProfile = localStorage.getItem('uscope_profile');
      if (lsProfile) return JSON.parse(lsProfile);
      return JSON.parse(sessionStorage.getItem('uscope_profile') || 'null');
    } catch { return null; }
  });
  // In-memory session query history — last 5 queries, used to populate previous_query on search
  const queryHistoryRef = React.useRef([]);

  // Identify this anonymous session in PostHog so behavioral events are grouped per session
  useEffect(() => {
    if (process.env.REACT_APP_POSTHOG_KEY) {
      posthog.identify(sessionId);
    }
  }, [sessionId]);

  // On mount, fetch saved profile from Supabase if we don't have one in session
  useEffect(() => {
    if (profile) return;
    fetch(`${API}/api/profile?session_id=${sessionId}`, {
      headers: { 'Authorization': `Bearer ${API_KEY}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.profile) {
          setProfile(d.profile);
          const json = JSON.stringify(d.profile);
          localStorage.setItem('uscope_profile', json);
          sessionStorage.setItem('uscope_profile', json);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  function saveProfile(p) {
    setProfile(p);
    const json = JSON.stringify(p);
    localStorage.setItem('uscope_profile', json);
    sessionStorage.setItem('uscope_profile', json);
  }

  function addToQueryHistory(query) {
    const h = queryHistoryRef.current;
    if (h[h.length - 1] === query) return; // dedupe consecutive
    queryHistoryRef.current = [...h, query].slice(-5);
  }

  function getPreviousQuery() {
    const h = queryHistoryRef.current;
    return h.length >= 2 ? h[h.length - 2] : null;
  }

  return (
    <UserContext.Provider value={{ sessionId, profile, saveProfile, addToQueryHistory, getPreviousQuery }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
